import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiError } from '@data-access/api/error';
import { UserOperationDataService, UserOperationMember } from '@data-access/api/user-operation';
import { HeapService } from '@feature/analytics';
import { FreshchatService } from '@feature/freshdesk';
import { AUTH_COOKIE_SESSION } from '@util/environment';
import {
  BehaviorSubject,
  defer,
  EMPTY,
  from,
  lastValueFrom,
  Observable,
  of,
  ReplaySubject,
  retry,
  throwError,
  timer,
} from 'rxjs';
import { catchError, filter, finalize, map, share, switchMap, tap } from 'rxjs/operators';
import { AuthLoginResult } from '../../interfaces/login-result.interface';
import { CpUserResponse } from '../../interfaces/user-response.interface';
import { AuthSecureStorageService } from '../secure-storage/auth-secure-storage.service';
import { AuthUsermanagementDataService } from '../usermanagement/usermanagement-data.service';

const RETRY_TIMEOUT: number = 1000;
const RETRY_COUNT: number = 30;
const COOKIE_SESSION_REFRESH_MARGIN_SECONDS: number = 30;
const SESSION_REJECTED_STATUSES: number[] = [400, 401];
const FINAL_FORBIDDEN_DETAILS: string[] = [
  'login.user.suspended',
  'login.twoFactor.usedOrExpired',
  'login.default.password.expired',
  'login.pin.password.expired',
];

class AuthSessionRejectedError extends Error {
  constructor(readonly generation: number) {
    super('The session was rejected.');
  }
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly usermanagementService: AuthUsermanagementDataService = inject(AuthUsermanagementDataService);
  private readonly userOperationDataService: UserOperationDataService = inject(UserOperationDataService);
  private readonly heapService: HeapService = inject(HeapService);
  private readonly freshchatService: FreshchatService = inject(FreshchatService);
  private readonly secureStorageService: AuthSecureStorageService = inject(AuthSecureStorageService);
  private readonly cookieSession: boolean = inject(AUTH_COOKIE_SESSION);

  private pendingLoginUser: CpUserResponse | null = null;
  private pendingRevoke: Promise<void> | null = null;
  private sharedRefresh$: Observable<CpUserResponse> | null = null;
  private sessionGeneration: number = 0;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  private readonly _user$: BehaviorSubject<CpUserResponse | null> = new BehaviorSubject<CpUserResponse | null>(null);
  // TODO: consider this, it is only used in MemberService for the username.
  readonly user$: Observable<CpUserResponse | null> = this._user$
    .asObservable()
    .pipe(filter((user: CpUserResponse | null) => !!user));

  readonly isLoggedIn$: Observable<boolean> = this._user$.pipe(
    tap((user: CpUserResponse | null) => user && this.heapService.identify(user.externalKey)),
    map((user: CpUserResponse | null) => !!user),
  );

  async init(): Promise<void> {
    this.secureStorageService.removeLegacySession();
    this._user$.next(await this.secureStorageService.getUserResponse().catch((): null => null));
  }

  login$(username: string, password: string, token?: string): Observable<AuthLoginResult> {
    return this.afterPendingRevoke$(this.usermanagementService.login$(username, password, token)).pipe(
      retry({
        count: token ? 0 : RETRY_COUNT,
        delay: (error: HttpErrorResponse) => {
          if (this.userHasNotYetBeenGrantedAccess(error)) {
            return timer(RETRY_TIMEOUT);
          }
          throw error;
        },
      }),
      switchMap((userResponse: CpUserResponse) =>
        this.isOtpRequired(userResponse)
          ? of<AuthLoginResult>({ otpRequired: true })
          : this.authenticate$(userResponse),
      ),
    );
  }

  isLoggedIn(): boolean {
    return !!this._user$.value;
  }

  isLoginPending(): boolean {
    return !!this.pendingLoginUser;
  }

  logout(): void {
    const hadSession: boolean = this.isLoginPending() || this.isLoggedIn();
    if (hadSession) {
      this.revokeCookieSession();
    }
    this.pendingLoginUser = null;
    this.beginSessionGeneration();
    void this.secureStorageService.clear();
    if (!hadSession) {
      return;
    }
    this._user$.next(null);
    this.heapService.resetIdentify();
    this.freshchatService.clearUser();
  }

  cancelPendingLogin(): boolean {
    if (!this.pendingLoginUser) {
      return false;
    }
    this.revokeCookieSession();
    this.pendingLoginUser = null;
    this.beginSessionGeneration();
    void this.secureStorageService.clear();
    return true;
  }

  getToken$(): Observable<string | null> {
    const session$: Observable<CpUserResponse | null> = this.pendingLoginUser
      ? of(this.pendingLoginUser)
      : this.ensureFreshSession$();
    return session$.pipe(map((user: CpUserResponse | null) => user?.accessToken ?? null));
  }

  refreshAfterUnauthorized$(): Observable<void> {
    if (!this.cookieSession || !this._user$.value) {
      return throwError(() => new AuthSessionRejectedError(this.sessionGeneration));
    }
    return this.startOrJoinRefresh$().pipe(map((): void => undefined));
  }

  isSessionRejected(error: unknown): boolean {
    return error instanceof AuthSessionRejectedError && this.isCurrentSession(error.generation);
  }

  private ensureFreshSession$(): Observable<CpUserResponse | null> {
    const user: CpUserResponse | null = this._user$.value;
    if (!user) {
      return of(null);
    }
    if (!this.isSessionExpiring(user)) {
      return of(user);
    }
    return this.refreshOrLogout$();
  }

  private isOtpRequired(userResponse: CpUserResponse): boolean {
    return userResponse.expirationDate === null || userResponse.expirationDate === undefined;
  }

  private authenticate$(userResponse: CpUserResponse): Observable<AuthLoginResult> {
    const generation: number = this.beginSessionGeneration();
    const sessionUser: CpUserResponse = this.toSessionUser(userResponse);
    const persisted: Promise<void> = this.secureStorageService
      .setUserResponse(sessionUser)
      .catch((error: unknown): never => {
        if (this.isCurrentSession(generation)) {
          this.pendingLoginUser = null;
        }
        throw error;
      });
    this.pendingLoginUser = sessionUser;
    return from(persisted).pipe(
      switchMap(() =>
        this.checkIfMemberExists$(sessionUser.userDetails.username).pipe(
          catchError(async (): Promise<never> => {
            if (this.isCurrentSession(generation)) {
              this.pendingLoginUser = null;
              this.revokeCookieSession();
              await this.secureStorageService.clear();
            }
            throw new ApiError('error.member-not-found');
          }),
        ),
      ),
      filter((): boolean => this.isCurrentSession(generation)),
      map((): AuthLoginResult => {
        this.pendingLoginUser = null;
        this._user$.next(sessionUser);
        return { otpRequired: false, user: sessionUser };
      }),
      finalize(() => {
        if (this.isCurrentSession(generation)) {
          this.cancelPendingLogin();
        }
      }),
    );
  }

  private beginSessionGeneration(): number {
    this.sharedRefresh$ = null;
    return ++this.sessionGeneration;
  }

  private isCurrentSession(generation: number): boolean {
    return generation === this.sessionGeneration;
  }

  private isSessionExpiring(user: CpUserResponse): boolean {
    const marginInSeconds: number = this.cookieSession ? COOKIE_SESSION_REFRESH_MARGIN_SECONDS : 0;
    const nowInSeconds: number = new Date().getTime() / 1000;
    return nowInSeconds + marginInSeconds >= user.expirationDate;
  }

  private refreshOrLogout$(): Observable<CpUserResponse> {
    const generation: number = this.sessionGeneration;
    return this.startOrJoinRefresh$().pipe(
      catchError((error: unknown): Observable<never> => {
        if (!this.isSessionRejected(error)) {
          return this.isCurrentSession(generation) ? throwError(() => error) : EMPTY;
        }
        this.logoutAfterFailedRefresh(generation);
        return EMPTY;
      }),
    );
  }

  private logoutAfterFailedRefresh(generation: number): void {
    if (!this.isCurrentSession(generation) || !this._user$.value) {
      return;
    }
    console.debug('Could not refresh session, logging out.');
    this.logout();
  }

  private startOrJoinRefresh$(): Observable<CpUserResponse> {
    if (!this.sharedRefresh$) {
      const generation: number = this.sessionGeneration;
      const refresh$: Observable<CpUserResponse> = this.usermanagementService.refresh$().pipe(
        catchError((error: unknown): Observable<never> => throwError(() => this.toRefreshError(error, generation))),
        switchMap((userResponse: CpUserResponse) => this.applyRefreshedSession(userResponse, generation)),
        finalize(() => {
          if (this.sharedRefresh$ === refresh$) {
            this.sharedRefresh$ = null;
          }
        }),
        share({
          connector: (): ReplaySubject<CpUserResponse> => new ReplaySubject<CpUserResponse>(1),
          resetOnError: false,
          resetOnComplete: false,
          resetOnRefCountZero: false,
        }),
      );
      this.sharedRefresh$ = refresh$;
    }
    return this.sharedRefresh$;
  }

  private toRefreshError(error: unknown, generation: number): unknown {
    const rejected: boolean = error instanceof HttpErrorResponse && SESSION_REJECTED_STATUSES.includes(error.status);
    return rejected ? new AuthSessionRejectedError(generation) : error;
  }

  private async applyRefreshedSession(userResponse: CpUserResponse, generation: number): Promise<CpUserResponse> {
    this.assertCurrentSession(generation);
    const sessionUser: CpUserResponse = this.toSessionUser(userResponse);
    await this.secureStorageService.setUserResponse(sessionUser);
    this.assertCurrentSession(generation);
    this._user$.next(sessionUser);
    return sessionUser;
  }

  private toSessionUser(userResponse: CpUserResponse): CpUserResponse {
    if (!this.cookieSession) {
      return userResponse;
    }
    const sessionUser: CpUserResponse = { ...userResponse };
    delete sessionUser.accessToken;
    return sessionUser;
  }

  private revokeCookieSession(): void {
    if (!this.cookieSession) {
      return;
    }
    const loggedOut: Promise<void> = this.sharedRefresh$
      ? lastValueFrom(this.sharedRefresh$)
          .catch((): void => undefined)
          .then(() => this.requestLogout())
      : this.requestLogout();
    const revoked: Promise<void> = Promise.all([this.pendingRevoke, loggedOut]).then((): void => {
      if (this.pendingRevoke === revoked) {
        this.pendingRevoke = null;
      }
    });
    this.pendingRevoke = revoked;
  }

  private requestLogout(): Promise<void> {
    return lastValueFrom(this.usermanagementService.logout$(), { defaultValue: undefined }).catch(
      (): void => undefined,
    );
  }

  private afterPendingRevoke$<T>(request$: Observable<T>): Observable<T> {
    return defer(() => this.pendingRevoke ?? of(undefined)).pipe(switchMap(() => request$));
  }

  private assertCurrentSession(generation: number): void {
    if (!this.isCurrentSession(generation)) {
      throw new Error('The session ended while it was being refreshed.');
    }
  }

  private checkIfMemberExists$(username: string): Observable<boolean> {
    return this.userOperationDataService.getMember$(username).pipe(
      retry({
        count: RETRY_COUNT,
        delay: (error: HttpErrorResponse) => {
          if (this.userHasNotYetBeenGrantedAccess(error)) {
            return timer(RETRY_TIMEOUT);
          }
          throw error;
        },
      }),
      map((member: boolean | UserOperationMember) => !!member),
    );
  }

  /*
   * The user is granted access to the member by askari-connect which is an async process. The
   * error response will be a 403 until the user has been granted access. A login with a one-time
   * code is never retried: the code is consumed by the first attempt.
   */
  private userHasNotYetBeenGrantedAccess(error: HttpErrorResponse | ApiError): boolean {
    const response: HttpErrorResponse | undefined = error instanceof HttpErrorResponse ? error : error.originalError;
    return response?.status === 403 && !FINAL_FORBIDDEN_DETAILS.includes(response.error?.['detail']);
  }
}
